import React, { useEffect, useState, useRef, useMemo } from 'react';
import { AssetClass } from '../types/assetClass';
import { CorrelationMatrix } from '../types/correlationMatrix';
import { Allocation, SimulationResult } from '../types/simulation';
import { simulatePortfolio } from '../utils/portfolioSimulation';

interface Props {
  assetClasses: AssetClass[];
  allocation: Partial<Allocation>;
  correlationMatrix: CorrelationMatrix;
  initialInvestment: number;
  monthlyContribution?: number;
  targetAmount?: number;
  years: number;
  iterations?: number;
}

interface ChartData {
  time: number[];
  percentiles: SimulationResult['percentiles'];
}

const PortfolioSimulationChart: React.FC<Props> = ({
  assetClasses,
  allocation,
  correlationMatrix,
  initialInvestment,
  years,
  iterations = 1000,
  monthlyContribution = 0,
  targetAmount = 0
}) => {
  const [chartData, setChartData] = useState<ChartData | null>(null);
  const [tableView, setTableView] = useState<'none' | 'end' | 'yearly'>('none');
  const [tooltip, setTooltip] = useState<{
    visible: boolean;
    x: number;
    y: number;
    values: {
      p95: number;
      p75: number;
      p50: number;
      p25: number;
      p05: number;
    };
  } | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const result = simulatePortfolio(
      assetClasses,
      allocation,
      correlationMatrix,
      initialInvestment,
      years,
      iterations,
      monthlyContribution
    );
    setChartData({
      time: result.time,
      percentiles: result.percentiles
    });
  }, [assetClasses, allocation, correlationMatrix, initialInvestment, years, iterations, monthlyContribution]);

  const endValues = useMemo(() => {
    if (!chartData) return [];
    return [
      { label: '95th', value: chartData.percentiles.p95[years] },
      { label: '75th', value: chartData.percentiles.p75[years] },
      { label: 'Median', value: chartData.percentiles.p50[years] },
      { label: '25th', value: chartData.percentiles.p25[years] },
      { label: '5th', value: chartData.percentiles.p05[years] }
    ];
  }, [chartData, years]);

  if (!chartData) return <div>Loading simulation...</div>;

  const maxValue = Math.max(
    ...chartData.percentiles.p95,
    ...chartData.percentiles.p75,
    ...chartData.percentiles.p50,
    ...chartData.percentiles.p25,
    ...chartData.percentiles.p05
  );
  const height = 300;
  const width = 800;
  const padding = { top: 20, right: 40, bottom: 40, left: 60 };

  const xScale = (x: number) => 
    (x / years) * (width - padding.left - padding.right) + padding.left;
  
  const yScale = (y: number) => 
    height - padding.bottom - ((y / maxValue) * (height - padding.top - padding.bottom));

  const formatCurrency = (value: number) => 
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);

  const generatePath = (data: number[]) => {
    return data.map((value, index) => 
      `${index === 0 ? 'M' : 'L'} ${xScale(index)} ${yScale(value)}`
    ).join(' ');
  };

  const handleMouseMove = (event: React.MouseEvent<SVGRectElement>) => {
    if (!chartData || !svgRef.current) return;

    const svgRect = svgRef.current.getBoundingClientRect();
    const mouseX = event.clientX - svgRect.left;
    
    // Convert mouse position to time index
    const timeIndex = Math.round(
      ((mouseX - padding.left) / (width - padding.left - padding.right)) * years
    );
    
    // Ensure timeIndex is within bounds
    if (timeIndex < 0 || timeIndex > years) {
      setTooltip(null);
      return;
    }

    setTooltip({
      visible: true,
      x: xScale(timeIndex),
      y: yScale(chartData.percentiles.p50[timeIndex]),
      values: {
        p95: chartData.percentiles.p95[timeIndex],
        p75: chartData.percentiles.p75[timeIndex],
        p50: chartData.percentiles.p50[timeIndex],
        p25: chartData.percentiles.p25[timeIndex],
        p05: chartData.percentiles.p05[timeIndex],
      }
    });
  };

  const handleMouseLeave = () => {
    setTooltip(null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
        <button
          onClick={() => setTableView(tableView === 'end' ? 'none' : 'end')}
          style={{
            padding: '8px 16px',
            backgroundColor: tableView === 'end' ? '#1565c0' : '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          {tableView === 'end' ? 'Hide End Values' : 'Show End Values'}
        </button>
        <button
          onClick={() => setTableView(tableView === 'yearly' ? 'none' : 'yearly')}
          style={{
            padding: '8px 16px',
            backgroundColor: tableView === 'yearly' ? '#1565c0' : '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          {tableView === 'yearly' ? 'Hide Yearly Values' : 'Show Yearly Values'}
        </button>
      </div>
      <svg ref={svgRef} width={width} height={height} style={{ backgroundColor: 'white' }}>
        {/* Y-axis grid lines and labels */}
        {[0, 0.25, 0.5, 0.75, 1].map(percent => {
          const y = yScale(maxValue * percent);
          return (
            <g key={percent}>
              <line
                x1={padding.left}
                y1={y}
                x2={width - padding.right}
                y2={y}
                stroke="#eee"
                strokeWidth={1}
              />
              <text
                x={padding.left - 10}
                y={y}
                textAnchor="end"
                dominantBaseline="middle"
                fontSize={12}
                fill="#666"
              >
                {formatCurrency(maxValue * percent)}
              </text>
            </g>
          );
        })}

        {/* X-axis grid lines and labels */}
        {chartData.time.map((year, index) => {
          const x = xScale(index);
          return (
            <g key={year}>
              <line
                x1={x}
                y1={padding.top}
                x2={x}
                y2={height - padding.bottom}
                stroke="#eee"
                strokeWidth={1}
              />
              <text
                x={x}
                y={height - padding.bottom + 20}
                textAnchor="middle"
                fontSize={12}
                fill="#666"
              >
                {year}
              </text>
            </g>
          );
        })}

        {/* Confidence intervals */}
        <path
          d={`${generatePath(chartData.percentiles.p95)} L ${xScale(years)} ${yScale(chartData.percentiles.p05[years])} ${generatePath(chartData.percentiles.p05.reverse())}`}
          fill="#e3f2fd"
          opacity={0.5}
        />
        <path
          d={`${generatePath(chartData.percentiles.p75)} L ${xScale(years)} ${yScale(chartData.percentiles.p25[years])} ${generatePath(chartData.percentiles.p25.reverse())}`}
          fill="#90caf9"
          opacity={0.5}
        />

        {/* Median line */}
        <path
          d={generatePath(chartData.percentiles.p50)}
          stroke="#1976d2"
          strokeWidth={2}
          fill="none"
        />

        {/* Axis labels */}
        <text
          x={width / 2}
          y={height - 5}
          textAnchor="middle"
          fontSize={12}
          fill="#666"
        >
          Years
        </text>
        <text
          x={-height / 2}
          y={15}
          textAnchor="middle"
          transform="rotate(-90)"
          fontSize={12}
          fill="#666"
        >
          Portfolio Value
        </text>

        {/* Legend */}
        <g transform={`translate(${padding.left}, ${padding.top})`}>
          <rect x={0} y={0} width={15} height={15} fill="#e3f2fd" />
          <text x={20} y={12} fontSize={12} fill="#666">90% Confidence</text>
          
          <rect x={120} y={0} width={15} height={15} fill="#90caf9" />
          <text x={140} y={12} fontSize={12} fill="#666">50% Confidence</text>
          
          <line x1={240} y1={7} x2={255} y2={7} stroke="#1976d2" strokeWidth={2} />
          <text x={260} y={12} fontSize={12} fill="#666">Median</text>
        </g>

        {/* Hover overlay */}
        <rect
          x={padding.left}
          y={padding.top}
          width={width - padding.left - padding.right}
          height={height - padding.top - padding.bottom}
          fill="transparent"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        />

        {/* Tooltip */}
        {tooltip && tooltip.visible && (
          <g>
            {/* Vertical line */}
            <line
              x1={tooltip.x}
              y1={padding.top}
              x2={tooltip.x}
              y2={height - padding.bottom}
              stroke="#666"
              strokeWidth={1}
              strokeDasharray="4,4"
            />
            
            {/* Tooltip box */}
            <g transform={`translate(${tooltip.x + 10}, ${padding.top + 10})`}>
              <rect
                x={0}
                y={0}
                width={150}
                height={120}
                fill="white"
                stroke="#ccc"
                rx={4}
              />
              <text x={10} y={20} fontSize={12} fill="#666">95th: {formatCurrency(tooltip.values.p95)}</text>
              <text x={10} y={40} fontSize={12} fill="#666">75th: {formatCurrency(tooltip.values.p75)}</text>
              <text x={10} y={60} fontSize={12} fill="#666">Median: {formatCurrency(tooltip.values.p50)}</text>
              <text x={10} y={80} fontSize={12} fill="#666">25th: {formatCurrency(tooltip.values.p25)}</text>
              <text x={10} y={100} fontSize={12} fill="#666">5th: {formatCurrency(tooltip.values.p05)}</text>
            </g>
          </g>
        )}
      </svg>

      {tableView === 'end' && (
        <table style={{ 
          width: '100%', 
          borderCollapse: 'collapse',
          backgroundColor: 'white',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          borderRadius: '4px'
        }}>
          <thead>
            <tr>
              <th style={{ 
                padding: '12px', 
                textAlign: 'left', 
                borderBottom: '2px solid #eee',
                color: '#666'
              }}>
                Percentile
              </th>
              <th style={{ 
                padding: '12px', 
                textAlign: 'right', 
                borderBottom: '2px solid #eee',
                color: '#666'
              }}>
                End Value (Year {years})
              </th>
            </tr>
          </thead>
          <tbody>
            {endValues.map(({ label, value }) => (
              <tr key={label}>
                <td style={{ 
                  padding: '12px',
                  borderBottom: '1px solid #eee',
                  color: '#666'
                }}>
                  {label}
                </td>
                <td style={{ 
                  padding: '12px',
                  borderBottom: '1px solid #eee',
                  textAlign: 'right',
                  fontFamily: 'monospace',
                  color: '#666'
                }}>
                  {formatCurrency(value)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Contribution and Growth Analysis */}
      <div style={{ 
        backgroundColor: 'white', 
        padding: '20px', 
        borderRadius: '6px',
        marginTop: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ marginBottom: '10px' }}>Investment Goal Analysis</h3>
        <div style={{ marginBottom: '20px', backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '4px' }}>
          <div style={{ marginBottom: '10px' }}>
            <strong>Initial Investment:</strong> {formatCurrency(initialInvestment)}
            {monthlyContribution > 0 && (
              <span style={{ marginLeft: '20px' }}>
                <strong>Monthly Contribution:</strong> {formatCurrency(monthlyContribution)}
              </span>
            )}
            {targetAmount > 0 && (
              <span style={{ marginLeft: '20px' }}>
                <strong>Target Amount:</strong> {formatCurrency(targetAmount)}
              </span>
            )}
          </div>
          {targetAmount > 0 && (
            <div style={{ 
              color: chartData.percentiles.p50[years] >= targetAmount ? '#4caf50' : '#f44336',
              fontWeight: 'bold'
            }}>
              {chartData.percentiles.p50[years] >= targetAmount ? (
                `Target likely to be achieved by year ${
                  chartData.percentiles.p50.findIndex(value => value >= targetAmount)
                }`
              ) : (
                <div>
                  <div>Target may not be achieved within the specified time horizon</div>
                  {(() => {
                    // Calculate required initial amount
                    const targetRatio = targetAmount / chartData.percentiles.p50[years];
                    const requiredInitial = Math.ceil(initialInvestment * targetRatio);
                    
                    // Calculate required monthly contribution
                    const additionalNeeded = targetAmount - chartData.percentiles.p50[years];
                    const requiredMonthly = Math.ceil(monthlyContribution + (additionalNeeded / (years * 12)));
                    
                    return (
                      <div style={{ marginTop: '10px', fontSize: '0.9em' }}>
                        <div>Suggestions to reach target:</div>
                        <div>• Increase initial investment to {formatCurrency(requiredInitial)}, or</div>
                        <div>• Increase monthly contribution to {formatCurrency(requiredMonthly)}</div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          )}
        </div>
        <p style={{ color: '#666', marginBottom: '20px' }}>
          This analysis shows how your portfolio could grow over time. The table below breaks down your yearly contributions
          and projected returns. Watch how your initial investment of {formatCurrency(initialInvestment)}
          {monthlyContribution > 0 ? ` plus monthly contributions of ${formatCurrency(monthlyContribution)}` : ''} 
          could grow through compound returns{targetAmount > 0 ? ` towards your target of ${formatCurrency(targetAmount)}` : ''}.
        </p>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ 
            width: '100%', 
            borderCollapse: 'collapse',
            backgroundColor: 'white',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            borderRadius: '4px'
          }}>
            <thead>
              <tr>
                <th style={{ 
                  padding: '12px', 
                  textAlign: 'left', 
                  borderBottom: '2px solid #eee',
                  color: '#666'
                }}>
                  Year
                </th>
                <th style={{ 
                  padding: '12px', 
                  textAlign: 'right', 
                  borderBottom: '2px solid #eee',
                  color: '#666'
                }}>
                  Yearly Contribution
                </th>
                <th style={{ 
                  padding: '12px', 
                  textAlign: 'right', 
                  borderBottom: '2px solid #eee',
                  color: '#666'
                }}>
                  Total Contributions
                </th>
                <th style={{ 
                  padding: '12px', 
                  textAlign: 'right', 
                  borderBottom: '2px solid #eee',
                  color: '#666'
                }}>
                  Median Expected Value
                </th>
                <th style={{ 
                  padding: '12px', 
                  textAlign: 'right', 
                  borderBottom: '2px solid #eee',
                  color: '#666'
                }}>
                  Investment Gains
                </th>
              </tr>
            </thead>
            <tbody>
              {chartData.time.map((year, index) => {
                const totalContributions = initialInvestment + (monthlyContribution * 12 * year);
                const medianValue = chartData.percentiles.p50[index];
                const investmentGains = medianValue - totalContributions;
                return (
                  <tr key={year}>
                    <td style={{ 
                      padding: '12px',
                      borderBottom: '1px solid #eee',
                      color: '#666'
                    }}>
                      {year}
                    </td>
                    <td style={{ 
                      padding: '12px',
                      borderBottom: '1px solid #eee',
                      textAlign: 'right',
                      fontFamily: 'monospace',
                      color: '#666'
                    }}>
                      {year === 0 ? '-' : formatCurrency(monthlyContribution * 12)}
                    </td>
                    <td style={{ 
                      padding: '12px',
                      borderBottom: '1px solid #eee',
                      textAlign: 'right',
                      fontFamily: 'monospace',
                      color: '#666'
                    }}>
                      {formatCurrency(totalContributions)}
                    </td>
                    <td style={{ 
                      padding: '12px',
                      borderBottom: '1px solid #eee',
                      textAlign: 'right',
                      fontFamily: 'monospace',
                      color: '#666'
                    }}>
                      {formatCurrency(medianValue)}
                    </td>
                    <td style={{ 
                      padding: '12px',
                      borderBottom: '1px solid #eee',
                      textAlign: 'right',
                      fontFamily: 'monospace',
                      color: investmentGains >= 0 ? '#4caf50' : '#f44336'
                    }}>
                      {formatCurrency(investmentGains)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {tableView === 'yearly' && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ 
            width: '100%', 
            borderCollapse: 'collapse',
            backgroundColor: 'white',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            borderRadius: '4px'
          }}>
            <thead>
              <tr>
                <th style={{ 
                  padding: '12px', 
                  textAlign: 'left', 
                  borderBottom: '2px solid #eee',
                  color: '#666',
                  position: 'sticky',
                  left: 0,
                  backgroundColor: 'white'
                }}>
                  Year
                </th>
                {['95th', '75th', 'Median', '25th', '5th'].map(label => (
                  <th key={label} style={{ 
                    padding: '12px', 
                    textAlign: 'right', 
                    borderBottom: '2px solid #eee',
                    color: '#666'
                  }}>
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {chartData.time.map((year, index) => (
                <tr key={year}>
                  <td style={{ 
                    padding: '12px',
                    borderBottom: '1px solid #eee',
                    color: '#666',
                    position: 'sticky',
                    left: 0,
                    backgroundColor: 'white'
                  }}>
                    {year}
                  </td>
                  <td style={{ 
                    padding: '12px',
                    borderBottom: '1px solid #eee',
                    textAlign: 'right',
                    fontFamily: 'monospace',
                    color: '#666'
                  }}>
                    {formatCurrency(chartData.percentiles.p95[index])}
                  </td>
                  <td style={{ 
                    padding: '12px',
                    borderBottom: '1px solid #eee',
                    textAlign: 'right',
                    fontFamily: 'monospace',
                    color: '#666'
                  }}>
                    {formatCurrency(chartData.percentiles.p75[index])}
                  </td>
                  <td style={{ 
                    padding: '12px',
                    borderBottom: '1px solid #eee',
                    textAlign: 'right',
                    fontFamily: 'monospace',
                    color: '#666'
                  }}>
                    {formatCurrency(chartData.percentiles.p50[index])}
                  </td>
                  <td style={{ 
                    padding: '12px',
                    borderBottom: '1px solid #eee',
                    textAlign: 'right',
                    fontFamily: 'monospace',
                    color: '#666'
                  }}>
                    {formatCurrency(chartData.percentiles.p25[index])}
                  </td>
                  <td style={{ 
                    padding: '12px',
                    borderBottom: '1px solid #eee',
                    textAlign: 'right',
                    fontFamily: 'monospace',
                    color: '#666'
                  }}>
                    {formatCurrency(chartData.percentiles.p05[index])}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PortfolioSimulationChart;
